import { getSession, getAutoToken } from '../utils/authUtils';
import { refreshIfPossible } from '../auth/backendRefresher';
import logger from '../utils/logger';
import { API_BASE_URL } from '../config/api';
import sessionManager from '../utils/sessionManager';

const isBrowser = () => typeof window !== 'undefined';

const safeGetItem = (storage, key) => {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (_) {
    return null;
  }
};

const resolveAdminViewingUserId = () => {
  if (!isBrowser()) return null;

  let adminViewingUserId = safeGetItem(window.sessionStorage, 'adminViewingUserId');
  if (adminViewingUserId) return adminViewingUserId;

  const adminAccessRaw = safeGetItem(window.sessionStorage, 'adminAccessData');
  if (adminAccessRaw) {
    try {
      const parsed = JSON.parse(adminAccessRaw);
      if (parsed?.targetUserId) {
        adminViewingUserId = String(parsed.targetUserId);
        window.sessionStorage.setItem('adminViewingUserId', adminViewingUserId);
        return adminViewingUserId;
      }
    } catch (_) { }
  }

  if (isBrowser()) {
    try {
      const params = new URLSearchParams(window.location?.search || '');
      const queryUserId = params.get('userId');
      if (queryUserId) {
        window.sessionStorage.setItem('adminViewingUserId', queryUserId);
        return queryUserId;
      }
      const tempKey = params.get('tempKey');
      if (tempKey) {
        const tempPayload = safeGetItem(window.sessionStorage, tempKey) || safeGetItem(window.localStorage, tempKey);
        if (tempPayload) {
          window.sessionStorage.setItem('adminAccessData', tempPayload);
          try {
            const parsed = JSON.parse(tempPayload);
            if (parsed?.targetUserId) {
              adminViewingUserId = String(parsed.targetUserId);
              window.sessionStorage.setItem('adminViewingUserId', adminViewingUserId);
              window.sessionStorage.removeItem(tempKey);
              window.localStorage.removeItem(tempKey);
              return adminViewingUserId;
            }
          } catch (_) { }
        }
      }

      const latestKey = safeGetItem(window.localStorage, 'latestAdminAccessTempKey');
      if (latestKey) {
        const latestPayload = safeGetItem(window.localStorage, latestKey);
        if (latestPayload) {
          window.sessionStorage.setItem('adminAccessData', latestPayload);
          try {
            const parsed = JSON.parse(latestPayload);
            if (parsed?.targetUserId) {
              adminViewingUserId = String(parsed.targetUserId);
              window.sessionStorage.setItem('adminViewingUserId', adminViewingUserId);
              return adminViewingUserId;
            }
          } catch (_) { }
        }
      }
    } catch (_) { }
  }

  return null;
};

const ensureUserIdParam = (endpoint, adminViewingUserId) => {
  if (!adminViewingUserId || !endpoint.includes('/gmb/')) return endpoint;
  const parts = endpoint.split('?');
  const base = parts[0];
  const params = new URLSearchParams(parts[1] || '');
  if (!params.has('userId')) {
    params.append('userId', adminViewingUserId);
  }
  const queryString = params.toString();
  return queryString ? `${base}?${queryString}` : base;
};
class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const adminViewingUserId = resolveAdminViewingUserId();
    const decoratedEndpoint = ensureUserIdParam(endpoint, adminViewingUserId);

    // Allow passing absolute URLs (e.g., for localhost during development)

    // Allow passing absolute URLs (e.g., for localhost during development)
    const isAbsolute = /^https?:\/\//i.test(decoratedEndpoint);
    const url = isAbsolute ? decoratedEndpoint : `${this.baseURL}${decoratedEndpoint}`;
    const session = getSession();
    const token = getAutoToken();
    const cleanedToken = typeof token === 'string'
      ? token.replace(/^Bearer\s+/i, '')
      : token;
    const bearer = cleanedToken
      ? `Bearer ${cleanedToken}`
      : undefined;

    // Debug logging
    console.log('🔐 API Request Auth:', {
      endpoint,
      hasSession: !!session,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN',
      bearer: bearer ? `${bearer.substring(0, 20)}...` : 'NO BEARER'
    });
    // Debug logging
    console.log('🔐 API Request Auth:', {
      endpoint,
      hasSession: !!session,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN',
      bearer: bearer ? `${bearer.substring(0, 20)}...` : 'NO BEARER'
    });

    // Check if body is FormData to handle file uploads
    const isFormData = options.body instanceof FormData;

    const config = {
      ...options,
      headers: {
        // Don't set Content-Type for FormData (browser sets it with boundary)
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...(bearer && { Authorization: bearer }),
        ...(options.headers || {}),
      },
    };

    try {
      let response = await fetch(url, config);

      let rawText = await response.text();

      let parsed;
      try {
        parsed = rawText ? JSON.parse(rawText) : null;
      } catch (_) {
        parsed = null;
      }

      if (!response.ok) {
        // If unauthorized, attempt refresh once and retry
        if (response.status === 401) {
          const refreshed = await refreshIfPossible(this.baseURL);
          const refreshedToken = getAutoToken(); // Get the updated token after refresh
          if (refreshed && refreshedToken) {
            const normalizedRefreshedToken = typeof refreshedToken === 'string'
              ? refreshedToken.replace(/^Bearer\s+/i, '')
              : refreshedToken;
            const refreshedBearer = normalizedRefreshedToken
              ? `Bearer ${normalizedRefreshedToken}`
              : undefined;
            const retryHeaders = {
              ...config.headers,
              ...(refreshedBearer && { Authorization: refreshedBearer })
            };
            response = await fetch(url, { ...config, headers: retryHeaders });
            rawText = await response.text();
            try {
              parsed = rawText ? JSON.parse(rawText) : null;
            } catch (_) {
              parsed = null;
            }
            if (response.ok) {
              return parsed;
            }
          }
        }
        const message = parsed?.message || parsed?.error || rawText || `HTTP error! status: ${response.status}`;
        const err = new Error(message);
        err.status = response.status;
        err.body = parsed || rawText;

        // Check if error indicates session expiration
        sessionManager.handleApiError(err);

        throw err;
      }

      return parsed;
    } catch (error) {
      logger.error('API request failed:', error);
      // Check if error indicates session expiration
      sessionManager.handleApiError(error);
      throw error;
    }
  }

  async get(endpoint, headers = {}) {
    return this.request(endpoint, {
      method: 'GET',
      headers,
    });
  }

  async post(endpoint, data, headers = {}) {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;

    return this.request(endpoint, {
      method: 'POST',
      headers,
      // Don't JSON.stringify FormData - send it as-is
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  async put(endpoint, data, headers = {}) {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;

    return this.request(endpoint, {
      method: 'PUT',
      headers,
      // Don't JSON.stringify FormData - send it as-is
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  async patch(endpoint, data, headers = {}) {
    // Check if data is FormData (for file uploads)
    const isFormData = data instanceof FormData;

    return this.request(endpoint, {
      method: 'PATCH',
      headers,
      // Don't JSON.stringify FormData - send it as-is
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  async delete(endpoint, headers = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      headers,
    });
  }

  /**
   * Get admin payments list
   * @param {Object} params - Query parameters (limit, offset, status, q)
   * @returns {Promise} Payments data
   */
  async getAdminPayments(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    if (params.status) queryParams.append('status', params.status);
    if (params.q) queryParams.append('q', params.q);

    const queryString = queryParams.toString();
    const endpoint = `/payments/all${queryString ? `?${queryString}` : ''}`;
    return this.get(endpoint);
  }

  /**
   * Run heatmap/competitor analysis using live Visibeen API
   * @param {Object} data - Analysis configuration
   * @returns {Promise} Analysis results
   */
  async runMaps(data) {
    // Use live Visibeen API endpoint - updated to correct endpoint name
    return this.post('/customer/profile/run-maps', data);
  }
}

const api = new ApiService();

export default api;
